import { isNotDefined } from '@sgme/fp';

import type { DealRejected, DealReply, FillReport } from '@/api/signalR/messages/execution.ts';
import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import { webapi, type WebapiEndpointBuilder } from '@/api/webapi.ts';
import { monitorTimeoutBulk } from '@/store/bulkTrade/api/monitorTimeoutBulk.ts';
import { selectBulkTrade } from '@/store/bulkTrade/selectors.ts';
import { bulkTradeSlice } from '@/store/bulkTrade/slice.ts';
import { computeAllSplittedLegsData } from '@/store/bulkTrade/utilities/margin.ts';
import type { AppDispatch, AppState, GetAppState } from '@/store/store.ts';
import { selectTabClientId } from '@/store/tabs/selectors.ts';
import type { ISODate, StringDateTime, Tenor } from '@/types/date.ts';
import { formatStringDateTime } from '@/utils/date.ts';
import { toBase64 } from '@/utils/toBase64.ts';

export type ExecuteBulkTradeRfsParams = {
  rfsId: string;
  tabId: string;
  signalRConnectionId: string;
  precision: number;
  userEmail: string;
  isUserInternalSales: boolean;
  lastQuoteFromBackTimestamp: number;
  lastQuoteReceivedByFrontTimestamp: number;
};

export type ExecuteBulkTradeRfsBody = {
  rfsId: string; // 'SGM_etgf7s0bi90dg0wzmpm8a5jfj';
  replyToStream: string; // 'EwOMIZlDwsmF_kUrZw_eWgby4rLgd02';
  quoteId: string; // 'to_be_accepted.215';
  userLocalDateTime: StringDateTime; // '2026-03-03T10:29:22';
  lastQuoteFromBackTimestamp: number; // 1772530161778;
  lastQuoteReceivedByFrontTimestamp: number; // 1772530161677;
  executionClickByFrontTimestamp: number; // 1772530162503;
  legs: ExecuteBulkTradeRfsBodyLeg[];
  email64NotificationList: string; // 'ZnJlZGVyaWMubWFzY2Fyb0BzZ2NpYi5jb20=';
};

export type ExecuteBulkTradeRfsBodyLeg = {
  amount: number; // 1000000;
  valueDate: ISODate; // '2026-03-05';
  valueDateTenor: Tenor; // 'SP';
  customerWay: 'Buy' | 'Sell';
  spotTrader: number; // 2.2525;
  spotClient: number; // 2.25243;
  forwardPoints: number; // 0;
};

export type ExecuteBulkTradeRfsResponse = {};

export const executeBulkTradeRfs = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<ExecuteBulkTradeRfsResponse, ExecuteBulkTradeRfsParams>({
    queryFn: async (params, api, _extraOptions, baseQuery) => {
      const state = api.getState() as AppState;
      const body = getExecuteBulkTradeRfsBody(params, state);

      const result = await baseQuery({
        url: '/fxBulk/rfs/execute',
        method: 'POST',
        body,
        timeout: 10_000, // 10 seconds
      });

      if (result.error) {
        return { error: result.error };
      }

      return { data: {} };
    },

    async onQueryStarted(request, { queryFulfilled, getState, dispatch }) {
      const stopMonitor = monitorTimeoutBulk(request.rfsId, dispatch, {
        onExecFail: () => {
          dispatch(
            bulkTradeSlice.actions.bulkTradeExecutionRejected({
              tabId: request.tabId,
              errorCode: 'TIMEOUT',
              reason: 'Deal not done',
            }),
          );
        },
        onExecSuccess: () => {
          dispatch(bulkTradeSlice.actions.executionSuccess({ tabId: request.tabId }));
        },
        getPendingExecutionDetails: () => {
          const currentState = getState() as AppState;
          const clientId = selectTabClientId(currentState, request.tabId);
          const bulkTrade = selectBulkTrade(currentState, request.tabId);

          if (isNotDefined(bulkTrade) || bulkTrade.step !== 'executed' || bulkTrade.status !== 'pending') {
            return undefined;
          }

          return {
            clientId: clientId,
            currencyPair: `${bulkTrade.currency1}/${bulkTrade.currency2}`,
            productType: 'FxBulkTrade',
            tradeDate: request.lastQuoteReceivedByFrontTimestamp,
          };
        },
      });

      try {
        await queryFulfilled;
      } catch {
        // if the initial query fails, we can stop the monitor and do not need to dispatch a timeout error as the error will be handled by the query's error handling
        stopMonitor();
        return;
      }
    },
    async onCacheEntryAdded(params, { cacheEntryRemoved, getState, dispatch }) {
      const state = getState() as AppState;

      const bulkTrade = selectBulkTrade(state, params.tabId);

      if (isNotDefined(bulkTrade)) {
        throw new Error(`BulkTrader with tabId ${params.tabId} not found`);
      }

      if (bulkTrade.step !== 'executed' || bulkTrade.status !== 'pending') {
        throw new Error(
          `BulkTrader with tabId ${params.tabId} is not in a valid step to be executed, current step is ${bulkTrade.step}`,
        );
      }

      const rfsId = bulkTrade.quote.RfsId;

      const handleReceiveDealReply = buildHandleReceiveDealReply(params, getState as GetAppState, dispatch);
      const handleReceiveDealRejected = buildHandleReceiveDealRejected(
        params,
        rfsId,
        getState as GetAppState,
        dispatch,
      );
      const handleReceiveFillReport = buildHandleReceiveFillReport(params, getState as GetAppState, dispatch);

      const signalRConnector = getSignalRConnector();
      signalRConnector.on('ReceiveDealReply', handleReceiveDealReply);
      signalRConnector.on('ReceiveDealRejected', handleReceiveDealRejected);
      signalRConnector.on('ReceiveFillReport', handleReceiveFillReport);

      // To close the listeners
      // const [ execute ] = useExecuteCashEspMutation({ fixedCacheKey: getExecuteBulkTradeRfsMutationCacheKey(tabId) });
      await cacheEntryRemoved;
      // To remove the cacheEntryRemoved, use:
      // dispatch(espApi.internalActions.removeMutationResult({ fixedCacheKey: executionId  }));

      signalRConnector.off('ReceiveDealReply', handleReceiveDealReply);
      signalRConnector.off('ReceiveDealRejected', handleReceiveDealRejected);
      signalRConnector.off('ReceiveFillReport', handleReceiveFillReport);
    },
  });
};

export const getExecuteBulkTradeRfsMutationCacheKey = (tabId: string) => `bulk-trade-rfs-execute-${tabId}`;

// ██████╗  ██████╗ ██████╗ ██╗   ██╗
// ██╔══██╗██╔═══██╗██╔══██╗╚██╗ ██╔╝
// ██████╔╝██║   ██║██║  ██║ ╚████╔╝
// ██╔══██╗██║   ██║██║  ██║  ╚██╔╝
// ██████╔╝╚██████╔╝██████╔╝   ██║
// ╚═════╝  ╚═════╝ ╚═════╝    ╚═╝

function getExecuteBulkTradeRfsBody(params: ExecuteBulkTradeRfsParams, state: AppState): ExecuteBulkTradeRfsBody {
  const bulkTrade = selectBulkTrade(state, params.tabId);

  if (isNotDefined(bulkTrade)) {
    throw new Error(`BulkTrader with tabId ${params.tabId} not found`);
  }

  if (bulkTrade.step !== 'executed' || bulkTrade.status !== 'pending') {
    throw new Error(
      `BulkTrader with tabId ${params.tabId} is not in a valid step to be executed, current step is ${bulkTrade.step}`,
    );
  }

  const legs = computeAllSplittedLegsData(bulkTrade, params.precision, params.isUserInternalSales);

  const preferedEmails = state.userPreferences.splitNotifications
    ? state.userPreferences.splitNotificationsEmailsCash
    : state.userPreferences.emails;

  const notificationEmails = preferedEmails.length > 0 ? preferedEmails.join(';') : params.userEmail;

  return {
    rfsId: bulkTrade.quote.RfsId,
    replyToStream: params.signalRConnectionId,
    quoteId: bulkTrade.quote.QuoteId,
    userLocalDateTime: formatStringDateTime(new Date()),
    lastQuoteFromBackTimestamp: params.lastQuoteFromBackTimestamp,
    lastQuoteReceivedByFrontTimestamp: params.lastQuoteReceivedByFrontTimestamp,
    executionClickByFrontTimestamp: Date.now(),
    legs: legs.map((leg) => ({
      amount: leg.amount,
      valueDate: leg.paymentDate,
      valueDateTenor: leg.paymentDateTenor,
      customerWay: leg.way,
      spotTrader: leg.spotWithMargin,
      spotClient: leg.spotWithMargin,
      forwardPoints: leg.forwardPointsWithMargin,
    })),
    email64NotificationList: toBase64(notificationEmails),
  };
}

// ███████╗██╗ ██████╗ ███╗   ██╗ █████╗ ██╗         ██████╗
// ██╔════╝██║██╔════╝ ████╗  ██║██╔══██╗██║         ██╔══██╗
// ███████╗██║██║  ███╗██╔██╗ ██║███████║██║         ██████╔╝
// ╚════██║██║██║   ██║██║╚██╗██║██╔══██║██║         ██╔══██╗
// ███████║██║╚██████╔╝██║ ╚████║██║  ██║███████╗    ██║  ██║
// ╚══════╝╚═╝ ╚═════╝ ╚═╝  ╚═══╝╚═╝  ╚═╝╚══════╝    ╚═╝  ╚═╝

// SEE: fx-web/app/src/Workers/SignalR/signalRStream.ts

const buildHandleReceiveDealReply = (
  params: ExecuteBulkTradeRfsParams,
  getState: GetAppState,
  dispatch: AppDispatch,
) => {
  return (dealReply: DealReply) => {
    const bulkTrade = selectBulkTrade(getState(), params.tabId);

    if (isNotDefined(bulkTrade) || bulkTrade.step !== 'executed' || bulkTrade.status !== 'pending') {
      return;
    }

    if (dealReply.Execute.RfsId !== bulkTrade.quote.RfsId) {
      return;
    }

    dispatch(
      bulkTradeSlice.actions.executionSuccess({
        tabId: params.tabId,
      }),
    );
  };
};

const buildHandleReceiveDealRejected = (
  params: ExecuteBulkTradeRfsParams,
  rfsId: string,
  _getState: GetAppState,
  dispatch: AppDispatch,
) => {
  return (dealRejected: DealRejected) => {
    if (dealRejected.Execute.RfsId !== rfsId) {
      return;
    }

    dispatch(
      webapi.internalActions.removeMutationResult({
        fixedCacheKey: getExecuteBulkTradeRfsMutationCacheKey(params.tabId),
      }),
    );

    dispatch(
      bulkTradeSlice.actions.bulkTradeExecutionRejected({
        tabId: params.tabId,
        errorCode: dealRejected.DealReject.ErrorCode,
        multipassErrorCode: dealRejected.DealReject.MultipassErrorCode,
        reason: dealRejected.DealReject.ErrorMessage,
        sessionId: dealRejected.DealReject.MultipassSessionId,
      }),
    );
  };
};

const buildHandleReceiveFillReport = (
  params: ExecuteBulkTradeRfsParams,
  getState: GetAppState,
  dispatch: AppDispatch,
) => {
  return (fillReport: FillReport) => {
    const bulkTrade = selectBulkTrade(getState(), params.tabId);

    if (
      isNotDefined(bulkTrade) ||
      bulkTrade.step !== 'executed' ||
      (bulkTrade.status !== 'pending' && bulkTrade.status !== 'done')
    ) {
      return;
    }

    if (fillReport.Execute.RfsId !== bulkTrade.quote.RfsId) {
      return;
    }

    dispatch(
      webapi.internalActions.removeMutationResult({
        fixedCacheKey: getExecuteBulkTradeRfsMutationCacheKey(params.tabId),
      }),
    );

    dispatch(
      bulkTradeSlice.actions.executionConfirmed({
        tabId: params.tabId,
        tradeIds: fillReport.BackOfficeReply.TradeIds,
      }),
    );
  };
};
